import { useEffect, useMemo, useState } from 'react';
import { Search } from '@mui/icons-material';
import { Box } from '@mui/material';
import { common } from '@mui/material/colors';
import dayjs from 'dayjs';
import { get } from 'lodash';
import { observer } from 'mobx-react-lite';
import { FuseSelect, ListPage, ListRow } from '/src/components';
import { useAdjustmentStore, useFilterStore, useUserStore } from '/src/context';
import { mediumDate } from '/src/lib/constants';
import { Note } from '/src/lib/models';
import { DataModelType } from '/src/lib/types';
import { currency } from '/src/utils';

/**
 * List Adjustments Page
 */
export const ListAdjustmentsPage: React.FC = observer(() => {
  const defaultSortType = 'sequenceNumber';
  const defaultSortReverse = false;
  const rowHeight = 48;

  /** Context **/
  const adjustmentStore = useAdjustmentStore();
  const filterStore = useFilterStore();
  const userStore = useUserStore();

  /** State **/
  const [search, setSearch] = useState<Record<string, string>>({});
  const [sortType, setSortType] = useState(defaultSortType);
  const [sortReverse, setSortReverse] = useState(defaultSortReverse);
  const [notes, setNotes] = useState<Note[]>();
  const [notesName, setNotesName] = useState<string>();

  /** Computed **/
  const adjustments = adjustmentStore.adjustments;
  const isFiltered = !!Object.values(search).length;

  const companies = useMemo(() => filterStore.companyListGet(['name', '_id', 'isLender']), [filterStore._companyList]);

  const listData = useMemo(() => {
    if (adjustments) {
      let list = adjustments.toSorted((a, b) => (get(a, sortType) > get(b, sortType) ? 1 : -1));
      if (sortReverse) list.reverse();

      if (search.companyId) list = list.filter((adjustment) => adjustment.company._id === search.companyId);

      return list;
    }
  }, [adjustments, search, sortType, sortReverse]);

  /** Methods **/

  const sortList = (type?: string) => {
    if (type) {
      if (sortType === type) setSortReverse(!sortReverse);
      else setSortType(type);
    }
  };

  const showNotesPopup = (notes?: Note[], name?: string) => {
    setNotes(notes ?? []);
    setNotesName(name);
  };

  /** Effects **/

  useEffect(() => {
    adjustmentStore.query();
  }, []);

  /** Render **/
  return (
    <ListPage
      data={listData}
      modelType={DataModelType.Adjustment}
      headerItems={[
        { flex: 5, propName: 'notes', label: 'Notes' },
        { propName: 'name', label: 'Name' },
        { flex: 15, propName: 'company.name', label: 'Company' },
        { propName: 'asset.name', label: 'Asset' },
        { flex: 10, propName: 'effectiveDate', label: 'Effective Date' },
        { propName: 'assetValue', label: 'Starting Asset Value', textAlign: 'right' },
        { propName: 'amount', label: 'Adjustment Amount', textAlign: 'right' },
        { propName: 'resultingAssetValue', label: 'Resulting Asset Value', textAlign: 'right' },
      ]}
      noFilteredDataMessage="No Adjustments found for that Company."
      toolbarItems={
        <>
          {userStore.isAdmin && (
            <Box display="flex" flex={1}>
              <Box display="flex" justifyContent="start" alignItems="center" gap="3px">
                <Search />
                <FuseSelect
                  value={search.companyId}
                  updateValue={(id) => setSearch({ ...search, companyId: id })}
                  options={companies.map((company) => ({ label: company.name, value: company._id }))}
                  placeholder="All Companies"
                  enablePlaceholderOption
                  disableUnderline
                  disableMargin
                  color={common.white}
                />
              </Box>
            </Box>
          )}
        </>
      }
      isFiltered={isFiltered}
      sortType={sortType}
      sortReverse={sortReverse}
      sortList={sortList}
      notes={notes}
      notesName={notesName}
      setNotes={setNotes}
      setNotesName={setNotesName}
    >
      {listData?.map((adjustment, i) => (
        <ListRow
          key={adjustment._id}
          rowHeight={rowHeight}
          padForScrollbar={listData.length > 8}
          items={[
            {
              propName: 'notes',
              isNotes: true,
              flex: 5,
              hide: !adjustment.notes.length,
              showNotesPopup: () => showNotesPopup(adjustment.notes, adjustment.name),
            },
            { propName: 'name', content: adjustment.name, url: `/adjustments/${adjustment._id}` },
            {
              propName: 'company.name',
              content: adjustment.company.name,
              url: `/companies/${adjustment.company._id}`,
              flex: 15,
            },
            { propName: 'asset.name', content: adjustment.asset?.name, url: `/assets/${adjustment.asset?._id}` },
            {
              propName: 'effectiveDate',
              content: dayjs(adjustment.effectiveDate).format(mediumDate),
              flex: 10,
            },
            {
              propName: 'assetValue',
              content: currency.format(adjustment.assetValue ?? ''),
              textAlign: 'right',
            },
            { propName: 'amount', content: currency.format(adjustment.amount), textAlign: 'right' },
            {
              propName: 'resultingAssetValue',
              content: currency.format(adjustment.resultingAssetValue ?? ''),
              textAlign: 'right',
            },
          ]}
          rowIndex={i}
        />
      ))}
    </ListPage>
  );
});
